// The workspace's list title: the current view as a menu of the others (Figma 1047:37201): My tickets
// (Sentinels only), All Kim tickets, Automation review, Waiting to be assigned and Follow up action, whose
// own filters open as a submenu (Figma 1070:47674). Benchmark reuses it with its own `labels` and "Cases".

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { MENU_ITEM, MENU_LABEL_ROW, MENU_META, MENU_PANEL, MENU_SUB_TRIGGER } from './workspace-menu-styles';
import { MenuCheck, MenuChevron, ViewChevron } from './workspace-menu-icons';
import { VIEW_LABELS, type ViewOption } from './view-options';

function RowLabel({ label, count, selected }: { label: string; count: number | undefined; selected: boolean }) {
  return (
    <span className={MENU_LABEL_ROW}>
      {/* Selected: Body-sm/Medium. Otherwise Body-sm/Regular. */}
      <span className={cn('truncate', selected ? 'font-medium' : 'font-normal')}>{label}</span>
      {count !== undefined && <span className={MENU_META}>{count}</span>}
    </span>
  );
}

export function ViewPicker<V extends string = ViewOption>({
  current,
  options,
  counts,
  onChange,
  followupMenu,
  labels = VIEW_LABELS as Record<V, string>,
  countUnit = 'Tickets',
}: {
  current: V;
  /** The rows this user gets, in VIEW_ORDER. */
  options: readonly V[];
  counts: Partial<Record<V, number>>;
  onChange: (option: V) => void;
  /** The Follow up action row's submenu (View / Workflow / Reason). Without it the row is a plain choice. */
  followupMenu?: React.ReactNode;
  /** Each option's name; the ticket list's by default. */
  labels?: Record<V, string>;
  /** What the count under the title counts. */
  countUnit?: string;
}) {
  const label = labels[current];
  const currentCount = counts[current];

  return (
    <>
      {/* The page's name for screen readers; the menu button below is how it reads on screen. */}
      <h1 className="sr-only">{label}</h1>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Figma 1070:46665: the name and its chevron 4px apart, the count 4px below. */}
          <Button
            variant="ghost"
            className="h-auto shrink-0 flex-col items-start justify-center gap-[4px] rounded-none p-0 hover:bg-transparent has-[>svg]:px-0"
          >
            <span className="flex items-center gap-[4px]">
              <span className="text-[16px] font-semibold leading-[1.1] whitespace-nowrap text-[#2b2e34]">{label}</span>
              <ViewChevron />
            </span>
            {currentCount !== undefined && (
              <span className="text-[10px] font-normal leading-[1.1] whitespace-nowrap text-[#9ca3af]">
                {currentCount} {countUnit}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        {/* Figma 1047:38318: 200px wide, its left edge 1px left of the title and 6px below it. */}
        <DropdownMenuContent
          align="start"
          alignOffset={-1}
          sideOffset={6}
          aria-label="Ticket view"
          className={cn(MENU_PANEL, 'w-[200px] min-w-0')}
        >
          {options.map((option) => {
            const selected = option === current;
            const row = <RowLabel label={labels[option]} count={counts[option]} selected={selected} />;
            if (option === 'followups' && followupMenu) {
              return (
                <DropdownMenuSub key={option}>
                  <DropdownMenuSubTrigger className={MENU_SUB_TRIGGER}>
                    {row}
                    {selected && <MenuCheck />}
                    <MenuChevron />
                  </DropdownMenuSubTrigger>
                  {/* Figma 1070:47914: 240px wide, 6px right of the switch. */}
                  <DropdownMenuSubContent sideOffset={6} className={cn(MENU_PANEL, 'w-[240px] min-w-0')}>
                    {followupMenu}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            }
            return (
              <DropdownMenuItem
                key={option}
                aria-checked={selected}
                role="menuitemradio"
                onSelect={() => onChange(option)}
                className={MENU_ITEM}
              >
                {row}
                {selected && <MenuCheck />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
